import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  Inject,
} from '@angular/core';
import { Permission } from '@account-users/account/models/Permission';
import {
  AccountStateAction,
  AccountState,
  IAccount,
} from '@account-users/account/models/IAccount';
import { IAccountUser } from '@account-users/account/models/IAccountUser';
import { UserRoles } from '@account-users/account/models/UserRoles';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AccountDetailsStateDialogComponent } from '../account-details-state-dialog/account-details-state-dialog.component';

export interface ActionButtons {
  name: string;
  action: AccountStateAction;
  enabled: boolean;
  class: string;
}

@Component({
  selector: 'app-account-details-buttons',
  templateUrl: './account-details-buttons.component.html',
  styleUrls: ['./account-details-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountDetailsButtonsComponent {
  @Input() isLoading = false;
  @Input() accountDetails: IAccount;
  @Input() userPermissions: Permission[] = [];
  @Input() accountHasFailedState = false;
  @Input() accountState: AccountState;
  @Input() isAlreadyOnAccount = false;
  @Input() isStaffUserOnAccount = false;
  @Input() accountId: string;
  @Input() users: IAccountUser[] = [];
  @Input() disabled = false;

  @Output() openInviteUserModal = new EventEmitter<void>();
  @Output() confirmAddStaffAdmin = new EventEmitter<void>();
  @Output() openInviteStaffUserModal = new EventEmitter<void>();
  @Output() openInvitePartnerUserModal = new EventEmitter<void>();
  @Output() deleteAccountSuccess = new EventEmitter<void>();
  @Output() accountActivate = new EventEmitter<void>();
  @Output() accountLock = new EventEmitter<void>();
  @Output() accountSuspend = new EventEmitter<void>();
  @Output() accountReactivate = new EventEmitter<void>();
  @Output() accountTerminate = new EventEmitter<void>();
  @Output() accountDelete = new EventEmitter<void>();
  @Output() accountDeleteCancel = new EventEmitter<void>();

  UserRoles = UserRoles;
  AccountState = AccountState;
  AccountStateAction = AccountStateAction;

  accountStateConfirmationModal: Partial<Record<AccountStateAction, string>>;
  accountStateTooltips: Partial<Record<AccountState, string>>;
  actionButtons: Partial<Record<AccountState, ActionButtons[]>>;

  constructor(
    @Inject('oneUrl') private oneUrl: string,
    private modalService: NgbModal,
  ) {
    this.actionButtons = {
      [AccountState.Success]: [
        {
          name: 'Lock account',
          action: AccountStateAction.Lock,
          enabled: true,
          class: 'btn btn-danger',
        },
      ],
      [AccountState.Locked]: [
        {
          name: 'Activate account',
          action: AccountStateAction.Active,
          enabled: true,
          class: 'btn btn-success',
        },
        {
          name: 'Suspend account',
          action: AccountStateAction.Suspend,
          enabled: true,
          class: 'btn btn-danger',
        },
      ],
      [AccountState.Suspended]: [
        {
          name: 'Reactivate account',
          action: AccountStateAction.Reactivate,
          enabled: true,
          class: 'btn btn-success',
        },
        {
          name: 'Terminate account',
          action: AccountStateAction.Terminate,
          enabled: true,
          class: 'btn btn-danger',
        },
      ],
      [AccountState.SuspendFailed]: [
        {
          name: 'Suspend account',
          action: AccountStateAction.Suspend,
          enabled: true,
          class: 'btn btn-success',
        },
      ],
      [AccountState.Terminated]: [
        {
          name: 'Reactivate account',
          action: AccountStateAction.Reactivate,
          enabled: true,
          class: 'btn btn-success',
        },
        {
          name: 'Delete account',
          action: AccountStateAction.Delete,
          enabled: true,
          class: 'btn btn-danger',
        },
      ],
      [AccountState.TerminateFailed]: [
        {
          name: 'Terminate account',
          action: AccountStateAction.Terminate,
          enabled: true,
          class: 'btn btn-danger',
        },
      ],
      [AccountState.DeleteScheduled]: [
        {
          name: 'Cancel account deletion',
          action: AccountStateAction.DeleteCancel,
          enabled: true,
          class: 'btn btn-danger',
        },
      ],
      [AccountState.DeleteFailed]: [
        {
          name: 'Delete account',
          action: AccountStateAction.Delete,
          enabled: true,
          class: 'btn btn-danger',
        },
      ],
      [AccountState.ResumeFailed]: [
        {
          name: 'Reactivate account',
          action: AccountStateAction.Reactivate,
          enabled: true,
          class: 'btn btn-success',
        },
      ],
    };

    this.accountStateConfirmationModal = {
      [AccountStateAction.Active]: 'You are about to ACTIVATE this account',
      [AccountStateAction.Reactivate]:
        'You are about to REACTIVATE this account',
      [AccountStateAction.Delete]: `You are about to DELETE this account. The following actions will be taken:
        All account data WILL BE IRREVERSIBLY REMOVED. This is permanent`,
      [AccountStateAction.DeleteCancel]: `You are about to CANCEL deletion for this account`,
      [AccountStateAction.Lock]: `You are about to LOCK this account. The following actions will be taken:
        All account users WILL NOT BE ABLE TO LOG IN.
        Activities, like scheduled Emails and SMS or Marketing Automation flows as well as all integrations, migrations, imports, tracked links and other data collection WILL CONTINUE TO WORK.`,
      [AccountStateAction.Terminate]: `You are about to TERMINATE this account. The following actions will be taken:
        Activities, like scheduled Emails and SMS or Marketing Automation flows as well as
        all integrations, migrations, imports, tracked links and other data collection WILL BE STOPPED.`,
      [AccountStateAction.Suspend]: `You are about to SUSPEND this account. The following actions will be taken:
        Scheduled Emails and SMS WILL BE PAUSED and Marketing Automation flows WILL BE STOPPED. All integrations, migrations, imports, tracked links and other data collection WILL CONTINUE TO WORK.`,
    };

    this.accountStateTooltips = {
      [AccountState.Locked]: `All account users ARE NOT ABLE TO LOG IN. Activities, like scheduled Emails and SMS or Marketing Automation flows
        as well as all integrations, migrations, imports, tracked links and other data collection CONTINUE TO WORK.`,
      [AccountState.Suspended]: `Scheduled Emails and SMS ARE PAUSED and Marketing Automation flows ARE STOPPED. All integrations, migrations, imports, tracked links and other data collection CONTINUE TO WORK.`,
      [AccountState.Terminated]: `Activities, like scheduled Emails and SMS or Marketing Automation flows as well as
        all integrations, migrations, imports, tracked links and other data collection ARE STOPPED.`,
    };
  }

  goToOneAsStaffUser() {
    window.open(`${this.oneUrl}?accountId=${this.accountId}`);
  }

  get inviteUserTooltipMessage() {
    if (this.accountHasFailedState) {
      return 'User invite will be disabled, until account has been bootstrapped';
    }

    return this.canInviteUsers
      ? 'Invite a new Apsis One user'
      : 'Sorry, you must be able to place orders to invite new users';
  }

  get canPlaceOrders() {
    return this.userPermissions.includes(Permission.CanPlaceOrders);
  }

  get canInviteUsers() {
    return this.userPermissions.includes(Permission.CanPlaceOrders);
  }

  get canAddStaff() {
    return this.userPermissions.includes(Permission.CanAddStaff);
  }

  get canInviteStaff() {
    return this.userPermissions.includes(Permission.CanInviteStaff);
  }

  get canInvitePartner() {
    return this.userPermissions.includes(Permission.CanInvitePartner);
  }

  get hasWawBackOffice() {
    return this.userPermissions.includes(Permission.WawBackOfficePreLaunch);
  }

  get addAsStaffTooltipMessage() {
    return this.canAddStaff
      ? ''
      : `You do not have the required permission for this action`;
  }

  get hasInviteButtonsDisabled() {
    return (
      this.accountState !== AccountState.Success
    );
  }

  public openDialog(action: AccountStateAction) {
    const dialog = this.modalService.open(AccountDetailsStateDialogComponent, {
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    const { componentInstance } = dialog;
    componentInstance.message = this.accountStateConfirmationModal[action];
    componentInstance.action = action;
    componentInstance.confirm.subscribe((stateAction: AccountStateAction) => {
      switch (stateAction) {
        case AccountStateAction.Active:
          this.accountActivate.emit();
          break;
        case AccountStateAction.Lock:
          this.accountLock.emit();
          break;
        case AccountStateAction.Suspend:
          this.accountSuspend.emit();
          break;
        case AccountStateAction.Reactivate:
          this.accountReactivate.emit();
          break;
        case AccountStateAction.Terminate:
          this.accountTerminate.emit();
          break;
        case AccountStateAction.Delete:
          this.accountDelete.emit();
          break;
        case AccountStateAction.DeleteCancel:
          this.accountDeleteCancel.emit();
          break;
      }
    });
  }
}
